/**
 * PATH: apps/ai/src/pages/dashboard/utils.ts
 * PURPOSE: Utility functions and hooks for the AI Hub DashboardPage
 */

import { useState } from 'react';

/** Custom hook that persists state to localStorage */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  return [storedValue, setValue];
}

/** Format large currency values with K/M suffixes */
export const formatCurrency = (val: number, decimals = 0): string => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(decimals)}K`;
  return `$${val.toFixed(decimals)}`;
};

/** Format percentage values */
export const formatPercent = (val: number, decimals = 1): string => `${val.toFixed(decimals)}%`;

/** Return a time-of-day greeting */
export const getTimeBasedGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};
